import { useState, useEffect } from 'react';
import axios from 'axios';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Spinner, Eye, EyeSlash, MagnifyingGlass } from '@phosphor-icons/react';
import { ROLE_LABELS } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  client_admin: 'bg-[#2980B9] text-white',
  client_employee: 'bg-[#2C3E50] text-white',
  client_user: 'bg-[#27AE60]/10 text-[#27AE60]',
};

export function CompanyUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'client_user' });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/my-company/users`);
      setUsers(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError('');
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/my-company/users`, formData);
      setUsers([res.data, ...users]);
      setDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'client_user' });
    } catch (err) {
      const d = err.response?.data?.detail;
      setFormError(typeof d === 'string' ? d : 'Erro ao criar usuario');
    } finally {
      setCreating(false);
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <DashboardLayout><div className="flex justify-center py-20"><Spinner size={48} className="animate-spin text-[#2C3E50]" /></div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div data-testid="company-users">
        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-[#1A252F] mb-2">Usuarios da Empresa</h1>
            <p className="text-[#5C6B7A]">Gerencie os usuarios da sua equipe</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setFormData({ name: '', email: '', password: '', role: 'client_user' }); setFormError(''); } }}>
            <DialogTrigger asChild>
              <Button className="bg-[#27AE60] hover:bg-[#219653]" data-testid="new-company-user-btn">
                <Plus size={18} className="mr-2" /> Novo Usuario
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-white border-0 shadow-2xl rounded-2xl p-0 overflow-hidden">
              <div className="relative px-8 pt-7 pb-5">
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[#27AE60] to-[#2C3E50]" />
                <DialogHeader>
                  <DialogTitle className="text-[#1A252F] text-xl font-bold">Novo Usuario</DialogTitle>
                </DialogHeader>
              </div>
              <form onSubmit={handleCreate} className="px-8 py-6 space-y-5" data-testid="company-user-form">
                {formError && <div className="p-3 rounded-lg bg-[#E74C3C]/10 border border-[#E74C3C]/20"><p className="text-sm text-[#E74C3C]">{formError}</p></div>}
                <div className="space-y-2">
                  <Label className="text-[#1A252F] font-semibold text-sm">Nome <span className="text-[#E74C3C]">*</span></Label>
                  <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Nome completo" required className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60]" data-testid="company-user-name" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1A252F] font-semibold text-sm">E-mail <span className="text-[#E74C3C]">*</span></Label>
                  <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="email@exemplo.com" required className="h-11 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60]" data-testid="company-user-email" />
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1A252F] font-semibold text-sm">Senha <span className="text-[#E74C3C]">*</span></Label>
                  <div className="relative">
                    <Input type={showPassword ? 'text' : 'password'} value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Minimo 6 caracteres" required minLength={6} className="h-11 pr-12 border-[#BDC3C7] bg-[#F8F9FA] focus:bg-white focus:border-[#27AE60]" data-testid="company-user-password" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#95A5A6] hover:text-[#5C6B7A]">
                      {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-[#1A252F] font-semibold text-sm">Tipo</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                    <SelectTrigger className="h-11 border-[#BDC3C7] bg-[#F8F9FA]" data-testid="company-user-role">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="client_employee">Funcionario</SelectItem>
                      <SelectItem value="client_user">Cliente Final</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex gap-3 pt-2 pb-2">
                  <Button type="button" variant="outline" className="flex-1 h-11" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" disabled={creating} className="flex-1 h-11 bg-[#27AE60] hover:bg-[#219653]" data-testid="save-company-user-btn">
                    {creating ? <Spinner size={18} className="animate-spin" /> : 'Criar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mb-6 relative max-w-md">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C6B7A]" />
          <Input placeholder="Pesquisar..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-12 bg-white border-[#BDC3C7]" data-testid="company-user-search" />
        </div>

        <div className="bg-white rounded-xl border border-[#2C3E50]/10 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-[#F8F9FA]">
                <TableHead className="font-semibold text-[#1A252F]">Nome</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">E-mail</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Tipo</TableHead>
                <TableHead className="font-semibold text-[#1A252F]">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map(u => (
                <TableRow key={u.id}>
                  <TableCell className="font-medium text-[#1A252F]">{u.name}</TableCell>
                  <TableCell className="text-[#5C6B7A]">{u.email}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100'}`}>
                      {ROLE_LABELS[u.role] || u.role}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${u.is_active !== false ? 'bg-[#27AE60]/10 text-[#27AE60]' : 'bg-[#E74C3C]/10 text-[#E74C3C]'}`}>
                      {u.is_active !== false ? 'Ativo' : 'Inativo'}
                    </span>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {filtered.length === 0 && <div className="py-12 text-center text-[#5C6B7A]">Nenhum usuario encontrado</div>}
        </div>
      </div>
    </DashboardLayout>
  );
}

export default CompanyUsers;
